import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { lineInterval } from 'views/report/utils/style-consts.ts';
import { getCostItemOverviewTotal, getNumberValue } from 'views/simulations/result-overview/result-utils';
import { addTitle } from 'views/report/utils/add-title/add-title.ts';

interface CostItem {
  cost_code_id: string;
  name: string;
  results: number[];
  children?: CostItem[];
}

export const addCostItemOverviewPage = async (
  doc: jsPDF,
  padding: number,
  forecastLogoHeight: number,
  simulationsResult: any,
  costCodesIdToCodeMap: Map<string | null, string>,
  primaryCurrency: string,
) => {
  doc.addPage();

  addTitle(doc, 'Cost Item Overview', {
    x: padding,
    y: forecastLogoHeight + padding + lineInterval,
  });

  const tableData = simulationsResult?.cost_results;

  const rows: RowInput[] = [];

  const createRow = (item: CostItem, depth: number): void => {
    rows.push([
      {
        content: item?.cost_code_id ? costCodesIdToCodeMap?.get(item?.cost_code_id) : '',
        styles: item.children && item.children.length > 0 ? { fillColor: [220, 220, 220] } : {}, // Grey background
      },
      {
        content: `${' '.repeat(depth * 4)}${item.name}`,
        styles: item.children && item.children.length > 0 ? { fillColor: [220, 220, 220] } : {}, // Grey background
      },
      {
        content: getNumberValue(item.results[0]),
        styles: item.children && item.children.length > 0 ? { fillColor: [220, 220, 220] } : {}, // Grey background
      },
      {
        content: getNumberValue(item.results[1]),
        styles: item.children && item.children.length > 0 ? { fillColor: [220, 220, 220] } : {}, // Grey background
      },
      {
        content: getNumberValue(item.results[2]),
        styles: item.children && item.children.length > 0 ? { fillColor: [220, 220, 220] } : {}, // Grey background
      },
      {
        content: getNumberValue(item.results[3]),
        styles: item.children && item.children.length > 0 ? { fillColor: [220, 220, 220] } : {}, // Grey background
      },
    ]);

    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => createRow(child, depth + 1));
    }
  };

  tableData?.forEach((item: CostItem, idx: number) => createRow(item, idx));
  const total = getCostItemOverviewTotal(simulationsResult).cells.map(({ value }, idx) =>
    idx === 1 ? { content: value, styles: { fontStyle: 'bold' } } : value,
  );
  const tableStartY = padding + forecastLogoHeight + 2 * lineInterval;
  autoTable(doc, {
    head: [
      [
        i18n.t(translations.code) as string,
        i18n.t(translations.projects_costItems) as string,
        'Mean',
        'P10',
        'P50',
        'P90',
      ],
      ['', '', primaryCurrency, primaryCurrency, primaryCurrency, primaryCurrency],
    ],
    body: [...rows, total as RowInput],
    startY: tableStartY,
    theme: 'plain',
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 'auto' },
      5: { cellWidth: 'auto' },
    },
    styles: {
      fontSize: 10,
      cellPadding: { top: 2, right: 2, bottom: 2, left: 0 },
      halign: 'left',
    },
    didDrawCell: (data: { cell: { x: number; y: number; width: number; height: number }; doc: jsPDF }) => {
      const { cell, doc } = data;
      const { x, y, width, height } = cell;
      // Draw bottom border for each cell
      doc.setDrawColor('#90908d');
      doc.setLineWidth(0.2);
      doc.line(x, y + height, x + width, y + height);
    },
    margin: { top: 30 },
  });
};
